/**
 * Shared list of agent + model combinations the UI lets a user pick
 * for a workflow (marketplace deploy footer + pipeline detail page).
 *
 * SOURCE OF TRUTH: as of the model-registry refactor (2026-05) the live
 * picker list comes from the backend `GET /models` endpoint, which derives
 * it from the canonical registry in @zibby/core. Use the `useAgentOptions()`
 * hook below to get the live list (with the hardcoded `AGENT_OPTIONS` array
 * as an offline fallback). Adding a model is now a BACKEND-only deploy.
 *
 * Frontend can't import @zibby/core (CRA/ESM, no package dep), so the fetch
 * bridge is how it stays in lockstep without duplicating the registry.
 *
 * Encoding: the option value is the string `<agent>:<model>` (e.g.
 * `"claude:opus-4.6"`). The empty string `""` is the "Project default"
 * sentinel — sends NO override, so the workflow row's aiAgent / aiModel
 * stay absent and the runtime falls back to project.aiAgent +
 * project.aiModel.
 */

import { useEffect, useState } from 'react';

// Labels are `<Vendor> · <Model>`. We can't drop the vendor prefix
// because the same model name reaches different CLIs (Sonnet 4.6 via
// Claude direct vs. via Cursor's router); without the prefix the user
// has no way to tell which strategy will run. Cursor in `auto` mode is
// the only entry whose model dimension is "the router picks" — we
// surface that as "Cursor · Auto" rather than burying it.
// ─────────────────────────────────────────────────────────────────────
// FALLBACK list. As of the model-registry refactor (2026-05) the picker is
// served LIVE by the backend `GET /models` endpoint, which derives the list
// from the canonical registry in @zibby/core. Adding a model is now a
// backend-only deploy.
//
// This hardcoded array is the OFFLINE FALLBACK — used verbatim if the
// `/models` fetch fails (network error, backend down) so the dropdown never
// empties. Keep it roughly in sync with the registry's pickerVisible set,
// but it does NOT need to be exhaustive: the live fetch is the source of
// truth and supersedes this whenever it succeeds.
//
// The `cursor:auto` entry lives ONLY here (not in the backend registry) —
// Cursor's router is a different concern (the backend registry tracks the
// direct claude/codex/gemini model maps), so we always merge it in.
// ─────────────────────────────────────────────────────────────────────
const PROJECT_DEFAULT_OPTION = { value: '', label: 'Project default' };
const CURSOR_OPTION = { value: 'cursor:auto', label: 'Cursor · Auto' };

export const AGENT_OPTIONS = [
  PROJECT_DEFAULT_OPTION,

  // Claude (Anthropic API direct)
  { value: 'claude:auto',               label: 'Claude · Sonnet 4.6' },
  { value: 'claude:opus-4.8',           label: 'Claude · Opus 4.8' },
  { value: 'claude:sonnet-4.8',         label: 'Claude · Sonnet 4.8' },
  { value: 'claude:opus-4.7',           label: 'Claude · Opus 4.7' },
  { value: 'claude:sonnet-4.7',         label: 'Claude · Sonnet 4.7' },
  { value: 'claude:opus-4.6',           label: 'Claude · Opus 4.6' },
  { value: 'claude:sonnet-4.5',         label: 'Claude · Sonnet 4.5' },
  { value: 'claude:opus-4.5',           label: 'Claude · Opus 4.5' },

  // Cursor (Cursor CLI — auto router picks the underlying model)
  CURSOR_OPTION,

  // Codex (OpenAI Codex CLI)
  { value: 'codex:o4-mini',             label: 'Codex · o4-mini' },
  { value: 'codex:gpt-5.5',             label: 'Codex · GPT-5.5' },
  { value: 'codex:gpt-5.4',             label: 'Codex · GPT-5.4' },
  { value: 'codex:gpt-5.3',             label: 'Codex · GPT-5.3' },
  { value: 'codex:gpt-5.2-codex',       label: 'Codex · GPT-5.2' },
  { value: 'codex:gpt-5.2',             label: 'Codex · GPT-5.2 base' },
  { value: 'codex:gpt-4o',              label: 'Codex · GPT-4o' },
  { value: 'codex:gpt-4o-mini',         label: 'Codex · GPT-4o mini' },

  // Gemini (Google Gemini CLI)
  { value: 'gemini:gemini-2.5-pro',     label: 'Gemini · 2.5 Pro' },
  { value: 'gemini:gemini-2.5-flash',   label: 'Gemini · 2.5 Flash' },
];

/**
 * Split an option value like `"claude:opus-4.6"` into `{ aiAgent, aiModel }`.
 * Returns `{ aiAgent: null, aiModel: null }` for the empty "project default"
 * sentinel so callers can use `?? null` chains cleanly.
 */
export function parseAgentOption(value) {
  if (!value || typeof value !== 'string') return { aiAgent: null, aiModel: null };
  const idx = value.indexOf(':');
  if (idx < 0) return { aiAgent: value, aiModel: null };
  return {
    aiAgent: value.slice(0, idx) || null,
    aiModel: value.slice(idx + 1) || null,
  };
}

/** Reverse of parseAgentOption — pick the matching option value. */
export function encodeAgentOption({ aiAgent, aiModel }) {
  if (!aiAgent) return '';
  return `${aiAgent}:${aiModel || 'auto'}`;
}

// ─────────────────────────────────────────────────────────────────────
// Model alias normalization.
//
// Project rows can store the aiModel in TWO formats depending on which
// surface wrote them:
//   - Marketplace deploy modal: short alias like `opus-4.6` (what we
//     ship in AGENT_OPTIONS).
//   - Project Settings → AI Settings: the full Anthropic SDK identifier
//     like `claude-sonnet-4-5-20250929` (what the agent SDK actually
//     expects on the wire).
// Both are valid — the cli / runtime normalizes via CLAUDE_MODEL_MAP at
// invocation time (see packages/core/src/constants.js). But the
// dropdown's `value === ${agent}:${model}` exact match doesn't know
// either form is the same model, so it gives up and shows "Pick an
// agent…" even though the project clearly has one configured.
//
// Mirror the canonical aliases here so we can find the right dropdown
// option regardless of which form the project row uses. Keep in lockstep
// with packages/core/src/constants.js — every key on the left must
// resolve to the SAME canonical value the agent-options shortlist uses
// (the short alias).
// ─────────────────────────────────────────────────────────────────────
const CLAUDE_TO_SHORT = {
  'claude-sonnet-4-6': 'auto',                  // current default
  'claude-sonnet-4-6-20250929': 'auto',
  'sonnet-4.6': 'auto',
  'sonnet-4-6': 'auto',
  'auto': 'auto',
  'claude-opus-4-6': 'opus-4.6',
  'opus-4.6': 'opus-4.6',
  'opus-4-6': 'opus-4.6',
  'claude-sonnet-4-5-20250929': 'sonnet-4.5',
  'sonnet-4.5': 'sonnet-4.5',
  'sonnet-4-5': 'sonnet-4.5',
  'claude-opus-4-20250514': 'opus-4.5',
  'opus-4.5': 'opus-4.5',
  'opus-4-5': 'opus-4.5',
};
const CODEX_TO_SHORT = {
  'o4-mini': 'o4-mini', 'o3': 'o3', 'o3-mini': 'o3-mini',
  'codex-mini': 'codex-mini', 'codex-mini-latest': 'codex-mini',
  'gpt-4o': 'gpt-4o', 'gpt-4o-mini': 'gpt-4o-mini',
  'gpt-5.2-codex': 'gpt-5.2-codex', 'gpt-5.2': 'gpt-5.2',
  'gpt-5.3': 'gpt-5.3', 'gpt-5.4': 'gpt-5.4',
  'auto': 'o4-mini',
};
const GEMINI_TO_SHORT = {
  'gemini-2.5-pro': 'gemini-2.5-pro',
  'gemini-2.5-flash': 'gemini-2.5-flash',
  'auto': 'gemini-2.5-pro',
};

const AGENT_TO_MAP = {
  claude: CLAUDE_TO_SHORT,
  codex: CODEX_TO_SHORT,
  gemini: GEMINI_TO_SHORT,
};

/**
 * Best-effort lookup of the dropdown option matching a project's
 * configured agent + model. Returns the option value string (e.g.
 * `'claude:sonnet-4.5'`) or empty string if no plausible match exists.
 *
 *   1. Direct hit on `${agent}:${model}` — fast path.
 *   2. Translate via the per-agent alias map (SDK ID → short alias)
 *      and retry — covers the Settings-page-style full IDs.
 *   3. Fall back to `${agent}:auto` if the agent exists in the
 *      dropdown but the specific model doesn't — user sees the
 *      right vendor, not a generic "Pick an agent" placeholder.
 *   4. Empty string if even the agent isn't in the shortlist.
 */
export function findAgentOptionValue({ aiAgent, aiModel }, options = AGENT_OPTIONS) {
  if (!aiAgent) return '';
  const direct = `${aiAgent}:${aiModel || 'auto'}`;
  if (options.some((o) => o.value === direct)) return direct;

  const aliasMap = AGENT_TO_MAP[aiAgent];
  if (aliasMap) {
    const short = aliasMap[aiModel] || aliasMap[String(aiModel || '').toLowerCase()];
    if (short) {
      const aliased = `${aiAgent}:${short}`;
      if (options.some((o) => o.value === aliased)) return aliased;
    }
  }

  // Cursor's only option is `cursor:auto`; if the project pinned a
  // weird cursor model, surface auto. Same for any agent — better to
  // show the vendor's first option than nothing.
  const fallback = options.find((o) => o.value.startsWith(`${aiAgent}:`));
  return fallback ? fallback.value : '';
}

// ─────────────────────────────────────────────────────────────────────
// Live picker — fetched from the backend `GET /models` endpoint.
// ─────────────────────────────────────────────────────────────────────

/**
 * Shape the backend payload into the dropdown's `{ value, label }[]`
 * contract, with the "Project default" sentinel first and the Cursor
 * router option merged in (Cursor isn't in the backend registry — it's a
 * separate concern, see the FALLBACK note above).
 *
 * @param {Array<{ value: string, label: string }>} backendModels
 * @returns {Array<{ value: string, label: string }>}
 */
function shapeAgentOptions(backendModels) {
  const opts = [PROJECT_DEFAULT_OPTION];
  // Insert cursor after the claude block to match the historical ordering
  // (claude → cursor → codex → gemini). We append it right after the last
  // claude entry; if no claude entries, just put it up front.
  let cursorInserted = false;
  let lastVendor = null;
  for (const m of backendModels) {
    const vendor = (m.value.split(':')[0]) || '';
    if (!cursorInserted && lastVendor === 'claude' && vendor !== 'claude') {
      opts.push(CURSOR_OPTION);
      cursorInserted = true;
    }
    opts.push({ value: m.value, label: m.label });
    lastVendor = vendor;
  }
  if (!cursorInserted) opts.push(CURSOR_OPTION);
  return opts;
}

// Module-level cache of the in-flight / resolved fetch so concurrently
// mounted pickers share ONE request and re-renders don't refetch.
let _agentOptionsPromise = null;

/**
 * Fetch the live agent/model options from `GET /models`. Resolves to the
 * shaped `{ value, label }[]`. On ANY failure resolves to the hardcoded
 * AGENT_OPTIONS fallback so the picker never empties. Cached module-level.
 *
 * @returns {Promise<Array<{ value: string, label: string }>>}
 */
export function fetchAgentOptions() {
  if (_agentOptionsPromise) return _agentOptionsPromise;
  _agentOptionsPromise = (async () => {
    try {
      // Lazy import to avoid a hard module dependency cycle (api ↔ auth).
      const api = require('../services/api').default;
      const res = await api.get('/models');
      const models = Array.isArray(res?.models) ? res.models : [];
      if (models.length === 0) return AGENT_OPTIONS;
      return shapeAgentOptions(models);
    } catch (err) {
      // Network/back-end failure → fall back to the hardcoded list. Reset
      // the cache so a later mount can retry rather than be stuck on the
      // fallback forever.
      // eslint-disable-next-line no-console
      console.warn('[agent-options] /models fetch failed, using fallback:', err?.message || err);
      _agentOptionsPromise = null;
      return AGENT_OPTIONS;
    }
  })();
  return _agentOptionsPromise;
}

/**
 * React hook: returns the live agent/model options (with `loading`). Starts
 * from the hardcoded AGENT_OPTIONS so the dropdown renders instantly, then
 * swaps in the live list once `/models` resolves. Multiple consumers share
 * one cached fetch.
 *
 * Drop-in for `AGENT_OPTIONS`: same `{ value, label }[]` shape, same
 * encoding contract — parseAgentOption / encodeAgentOption / findAgentOptionValue
 * all keep working against the returned array.
 *
 * @returns {{ options: Array<{ value: string, label: string }>, loading: boolean }}
 */
export function useAgentOptions() {
  const [options, setOptions] = useState(AGENT_OPTIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchAgentOptions().then((opts) => {
      if (!alive) return;
      setOptions(opts);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  return { options, loading };
}
